/**
 * KANONISKE SPØRGSMÅL OG BEREGNINGSPARAMETRE
 *
 * ⚠️  RET ALDRIG DISSE TAL uden at opdatere Excel-arket og CLAUDE.md.
 *     Kilde: Matematikken_på_testen_v2.xlsx (verificeret)
 *     Kontakt: Morten Dalum — morten@droemmevillaen.dk
 */

export type SvarMulighed = 'ingenting' | 'noget' | 'enStorDel';

const BASE_URL = 'https://www.droemmevillaen.dk/podcast';

/**
 * Slugs verificeret live på droemmevillaen.dk (alle 10 tjekket 2026-03-18).
 * Webflow bruger: å→aa, ø→oe, æ→ae.
 */
const SLUGS: Record<string, string> = {
  Ep47:  'ep47-total-hoved-eller-fagentreprise',
  Ep52:  'ep52-faa-det-optimale-ud-af-dine-m2',
  Ep74:  'ep74-en-aegte-selvbygger',
  Ep85:  'ep85-goer-det-fedt-med-mindre-plads',
  Ep90:  'ep90-solceller-paa-taget',
  Ep101: 'ep101-passivhuse-for-klimaets-skyld',
  Ep118: 'ep118-varmepumpens-magi',
  Ep139: 'ep139-en-selvbyggers-udfordringer',
  Ep165: 'ep165-bygherreleverancer-giver-frihed-og-risici',
  Ep175: 'ep175-eu-krav-og-nybyggeri',
};

export function podcastUrl(ep: string): string {
  const slug = SLUGS[ep];
  return slug ? `${BASE_URL}/${slug}` : BASE_URL;
}

export interface Question {
  readonly id: number;
  readonly key: string;
  readonly title: string;
  readonly description: string;
  /** Andel af budgettet dette spørgsmål vedrører (bruges i beregning) */
  readonly andel: number;
  readonly pct: Readonly<Record<SvarMulighed, readonly [number, number]>>;
  readonly podcastEp: readonly string[];
  readonly podcastTitles: readonly string[];
}

export const QUESTIONS: readonly Question[] = [
  {
    id: 1,
    key: 'self_work',
    title: 'Gør det selv',
    description: 'Hvor meget arbejde vil I selv bidrage med?',
    andel: 0.40,
    pct: {
      ingenting: [0, 0.02],
      noget:     [0.04, 0.08],
      enStorDel: [0.08, 0.12],
    },
    podcastEp: ['Ep74', 'Ep139'],
    podcastTitles: ['En ægte selvbygger', 'En selvbyggers udfordringer'],
  },
  {
    id: 2,
    key: 'owner_supply',
    title: 'Bygherreleverancer',
    description: 'I hvor høj grad vil I selv købe og levere materialer direkte?',
    andel: 0.15,
    pct: {
      ingenting: [0, 0],
      noget:     [0.03, 0.08],
      enStorDel: [0.12, 0.20],
    },
    podcastEp: ['Ep165'],
    podcastTitles: ['Bygherreleverancer giver frihed og risici'],
  },
  {
    id: 3,
    key: 'multiple_quotes',
    title: 'Hent flere tilbud',
    description: 'Henter I tilbud fra flere håndværkere og leverandører?',
    andel: 0.25,
    pct: {
      ingenting: [0, 0],
      noget:     [0.05, 0.12],
      enStorDel: [0.15, 0.20],
    },
    podcastEp: ['Ep47'],
    podcastTitles: ['Total-, hoved- eller fagentreprise?'],
  },
  {
    id: 4,
    key: 'enterprise_form',
    title: 'Enterpriseform',
    description: 'Har I overvejet hvilken entrepriseform der passer jer?',
    andel: 0.20,
    pct: {
      ingenting: [0, 0],
      noget:     [0.025, 0.075],
      enStorDel: [0.10, 0.20],
    },
    podcastEp: ['Ep47'],
    podcastTitles: ['Total-, hoved- eller fagentreprise?'],
  },
  {
    id: 5,
    key: 'fewer_sqm',
    title: 'Byg færre kvm',
    description: 'Overvejer I at optimere antallet af kvadratmeter?',
    andel: 0.30,
    pct: {
      ingenting: [0, 0],
      noget:     [0.10, 0.15],
      enStorDel: [0.20, 0.25],
    },
    podcastEp: ['Ep85', 'Ep52'],
    podcastTitles: ['Gør det fedt med mindre plads', 'Få det optimale ud af dine m²'],
  },
  {
    id: 6,
    key: 'energy',
    title: 'Energioptimering',
    description: 'Investerer I aktivt i energioptimering og lavere driftsomkostninger?',
    andel: 1.00,
    // ⚠️ VIGTIGT: ingenting = NEGATIV lav-besparelse (-2% i worst case)
    // Lav (pessimistisk): -2% meromkostning til drift — vis med rød farve i UI
    // Høj (optimistisk): 0% — ingen gevinst, men heller ingen straf
    pct: {
      ingenting: [-0.02, 0],
      noget:     [0, 0.005],
      enStorDel: [0.01, 0.02],
    },
    podcastEp: ['Ep101', 'Ep90', 'Ep118'],
    podcastTitles: ['Passivhuse', 'Solceller på taget', 'Varmepumpens magi'],
  },
  {
    id: 7,
    key: 'subsidies',
    title: 'Udnyt fradrag og tilskud',
    description: 'Undersøger I aktivt fradrag, tilskud og støtteordninger?',
    andel: 1.00,
    pct: {
      ingenting: [0, 0],
      noget:     [0.0025, 0.0075],
      enStorDel: [0.01, 0.02],
    },
    podcastEp: ['Ep175'],
    podcastTitles: ['EU krav og nybyggeri'],
  },
] as const;

/** Budget-slider konfiguration */
export const BUDGET_CONFIG = {
  min: 1_000_000,
  max: 10_000_000,
  step: 100_000,
  default: 3_600_000,
} as const;
